/**
 * SetOverseeingBcbaModal — set or replace a client's single Overseeing BCBA
 * (a.k.a. "Assigned BCBA" / owner of record → ClientMember.supervisingClinicianId).
 *
 * Mirrors the Set/Replace flow from the Assignments page so it can be triggered
 * per-row from the Clients list. Uses the existing setOverseeingBcba endpoint,
 * which replaces any current holder.
 *
 * 2-phase when replacing an owner with open Red Dots:
 *   Phase 1 — BCBA select + "Effective from" (unchanged).
 *   Phase 2 — only shown when the caseload-conflict check finds open Red Dots AND the selected
 *             BCBA differs from the current owner: pick a disposition (TRANSFER_TO_NEW /
 *             KEEP_WITH_OUTGOING / UNASSIGN) before saving.
 * Zero open Red Dots, or re-selecting the current owner (a true no-op server-side), saves
 * immediately with no extra step. A scheduled (future-dated) save that skips phase 2 still sends
 * `redDotDisposition: 'TRANSFER_TO_NEW'` explicitly, since Red Dots may open between now and the
 * effective date — this makes the applied-at-effective-date default explicit rather than relying
 * on the server's fallback.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import clientService, {
  type RedDotDisposition,
  type WorkflowPermissions,
} from '../../services/api/clientService';
import { useAllAssignableProviders, useCaseloadConflict } from '../../hooks/useClients';
import { RedDotDispositionOptions } from './RedDotDispositionOptions';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { SearchableSelect } from '../ui/SearchableSelect';
import { DatePicker } from '../ui/DatePicker';
import { formatDisplayDate, todayISO } from '../../utils/dateUtils';
import { formatPersonMeta } from '../shared/PersonMeta';

interface SetOverseeingBcbaModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
  /** Current owner's USER id (ClientMember.supervisingClinicianId), if any. */
  currentSupervisingClinicianId?: string | null;
  /** Current owner's display name, for the "replaces" note. */
  currentSupervisingClinicianName?: string | null;
  onSaved?: () => void;
  /** Raise when opened from inside another overlay so this stacks above it. */
  zIndex?: number;
}

type Phase = 'select' | 'disposition';

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Label = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Note = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const ErrorNote = styled(Note)`
  color: ${({ theme }) => theme.colors.status.error.text};
`;

const WarningNote = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.warning.text};
  background: ${({ theme }) => theme.colors.status.warning.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  margin: 0;
`;

const DispositionHeading = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing[3]};
`;

const EffectiveRecap = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: ${({ theme }) => theme.spacing[3]} 0 0;
`;

const fullName = (m: { firstName: string; lastName: string }) =>
  `${m.firstName} ${m.lastName}`.trim();

const errorMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

/** Display name per workflow key — the only place to touch when a new workflow is added. */
const WORKFLOW_LABELS: { key: 'redDot' | 'preference'; label: string }[] = [
  { key: 'redDot', label: 'Red Dot' },
  { key: 'preference', label: 'Preference Assessment' },
];

/** "Red Dot", "Red Dot and Preference Assessment", or '' when nothing is missing. */
const missingManageWorkflows = (permissions: WorkflowPermissions | undefined): string => {
  if (!permissions) return '';
  const missing = WORKFLOW_LABELS.filter((w) => permissions[w.key] !== 'MANAGE').map(
    (w) => w.label,
  );
  return missing.join(' and ');
};

export function SetOverseeingBcbaModal({
  isOpen,
  onClose,
  clientId,
  clientName,
  currentSupervisingClinicianId,
  currentSupervisingClinicianName,
  onSaved,
  zIndex,
}: SetOverseeingBcbaModalProps) {
  const queryClient = useQueryClient();
  const [memberId, setMemberId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [error, setError] = useState('');
  const [initialised, setInitialised] = useState(false);
  const [phase, setPhase] = useState<Phase>('select');
  const [disposition, setDisposition] = useState<RedDotDisposition>('TRANSFER_TO_NEW');

  // Source the BCBA options from the clients-scoped assignable-providers endpoint
  // (authorized by clients:manage), not the team:manage-guarded member hierarchy —
  // otherwise client managers without team access get a 403 here.
  const { data: providers, isLoading: providersLoading } = useAllAssignableProviders(isOpen);

  const members = useMemo(() => providers ?? [], [providers]);

  const memberOptions = useMemo(
    () =>
      members.map((m) => ({
        value: m.memberId,
        label: fullName(m),
        meta: formatPersonMeta({ centerName: m.centerName, roleName: m.roleName }),
        searchText: (m.roleNames ?? []).join(' '),
      })),
    [members],
  );

  // Pre-select the current holder (map its userId → member id) once providers load.
  if (isOpen && !initialised && members.length > 0) {
    if (currentSupervisingClinicianId) {
      const current = members.find((m) => m.userId === currentSupervisingClinicianId);
      if (current) setMemberId(current.memberId);
    }
    setInitialised(true);
  }

  // Fetched reactively once a BCBA is selected — usually resolved by the time the user
  // clicks Save, so phase 2 (if needed) appears without an extra loading step.
  const conflictQuery = useCaseloadConflict(clientId, memberId || undefined, {
    enabled: isOpen && !!memberId,
  });
  const openRedDotCount = conflictQuery.data?.openRedDotCount ?? 0;

  const selectedMember = useMemo(
    () => members.find((m) => m.memberId === memberId),
    [members, memberId],
  );

  // Advisory only — never blocks Save. Shown for whoever is selected, current holder or not.
  const missingWorkflows = useMemo(
    () => missingManageWorkflows(selectedMember?.workflowPermissions),
    [selectedMember],
  );
  const conflictOwner = conflictQuery.data?.currentOwner;
  const outgoingOwnerName = useMemo(
    () =>
      conflictOwner ? fullName(conflictOwner) : (currentSupervisingClinicianName ?? 'the outgoing BCBA'),
    [conflictOwner, currentSupervisingClinicianName],
  );

  // Re-selecting the current owner is a true no-op server-side — never prompt for a
  // disposition in that case. Prefer the conflict check's authoritative owner; fall
  // back to the prop while the query is in flight.
  const isSameOwner = selectedMember
    ? selectedMember.userId === (conflictOwner?.userId ?? currentSupervisingClinicianId)
    : false;

  const mutation = useMutation({
    mutationFn: () => {
      // Phase 2 skipped (0 open Red Dots today) on a future-dated owner change: send the
      // TRANSFER_TO_NEW default explicitly — Red Dots may open before the effective date,
      // and the applied-at-effective-date behavior should be stated, not implied.
      const scheduledDefault =
        !!effectiveFrom && effectiveFrom > todayISO() && !isSameOwner
          ? ('TRANSFER_TO_NEW' as const)
          : undefined;
      return clientService.setOverseeingBcba(
        clientId,
        memberId,
        effectiveFrom || undefined,
        phase === 'disposition' ? disposition : scheduledDefault,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      // Also refresh the open client detail so a drawer launching this modal updates
      // its header/facts strip immediately.
      queryClient.invalidateQueries({ queryKey: ['clients', 'detail', clientId] });
      // A future effectiveFrom schedules a PendingCaseloadTransfer instead of flipping the
      // owner immediately — refresh the pending-transfer banner so it reflects the new/updated
      // row right away (matches the explicit invalidation in useAssignProvidersToClient).
      queryClient.invalidateQueries({ queryKey: ['clients', 'pending-transfer', clientId] });
      onSaved?.();
      handleClose();
    },
    onError: (err: unknown) => setError(errorMessage(err, 'Failed to set overseeing BCBA')),
  });

  const resetState = () => {
    setMemberId('');
    setEffectiveFrom('');
    setError('');
    setInitialised(false);
    setPhase('select');
    setDisposition('TRANSFER_TO_NEW');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSave = () => {
    setError('');
    if (!memberId) {
      setError('Select a BCBA.');
      return;
    }
    if (phase === 'select' && openRedDotCount > 0 && !isSameOwner) {
      setPhase('disposition');
      return;
    }
    mutation.mutate();
  };

  const handleBack = () => {
    setError('');
    setPhase('select');
  };

  const isFutureEffective = !!effectiveFrom && effectiveFrom > todayISO();
  const waitingOnConflictCheck = !!memberId && conflictQuery.isLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={currentSupervisingClinicianId ? 'Replace Overseeing BCBA' : 'Set Overseeing BCBA'}
      size="md"
      zIndex={zIndex}
      footer={
        phase === 'disposition' ? (
          <>
            <Button variant="ghost" onClick={handleBack}>
              Back
            </Button>
            <Button variant="primary" isLoading={mutation.isPending} onClick={handleSave}>
              Confirm
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isLoading={mutation.isPending || waitingOnConflictCheck}
              onClick={handleSave}
            >
              Save
            </Button>
          </>
        )
      }
    >
      {phase === 'select' ? (
        <>
          <Field>
            <Label>Client</Label>
            <Note>{clientName}</Note>
          </Field>
          <Field>
            <Label>Overseeing BCBA (single active holder)</Label>
            <SearchableSelect
              options={memberOptions}
              value={memberId}
              onChange={setMemberId}
              placeholder="Select a BCBA…"
              aria-label="Overseeing BCBA"
              isLoading={providersLoading}
            />
            {currentSupervisingClinicianName && (
              <Note>
                Replaces current holder: <strong>{currentSupervisingClinicianName}</strong>
              </Note>
            )}
            {selectedMember && missingWorkflows && (
              <WarningNote>
                {fullName(selectedMember)} does not have manage permission on {missingWorkflows}.
                They can still be set as the Overseeing BCBA.
              </WarningNote>
            )}
          </Field>
          <Field>
            <Label>Effective from (optional, defaults today)</Label>
            <DatePicker
              value={effectiveFrom}
              onChange={setEffectiveFrom}
              placeholder="Effective from"
            />
          </Field>
          {error && <ErrorNote>{error}</ErrorNote>}
        </>
      ) : (
        <>
          <DispositionHeading>
            This client has {openRedDotCount} open Red Dot{openRedDotCount === 1 ? '' : 's'}
          </DispositionHeading>
          <RedDotDispositionOptions
            value={disposition}
            onChange={setDisposition}
            openRedDotCount={openRedDotCount}
            newOwnerName={selectedMember ? fullName(selectedMember) : 'the new BCBA'}
            outgoingOwnerName={outgoingOwnerName}
          />
          <EffectiveRecap>
            {isFutureEffective
              ? `Takes effect ${formatDisplayDate(effectiveFrom)}.`
              : 'Takes effect immediately.'}
          </EffectiveRecap>
          {error && <ErrorNote>{error}</ErrorNote>}
        </>
      )}
    </Modal>
  );
}

export default SetOverseeingBcbaModal;
